import { useOutletContext } from "react-router-dom";
import Accordian from "../../../elements/Accordian";
import { organizationTeamItem } from "../../../../services/organizationService";
import { useState, useMemo, useCallback, memo } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { lngDetect } from "../../../../helpers/lngDetect";
import { t } from "i18next";

// Constants
const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const RETRY_COUNT = 2;
const DEFAULT_LIMIT = 10;
const DEFAULT_PAGE = 1;

// Loading skeleton component
const LoadingSkeleton = memo(({ rows = 3 }) => (
  <div className="space-y-3">
    {Array.from({ length: rows }).map((_, index) => (
      <div key={index} className="animate-pulse">
        <div className="flex items-center justify-between py-3 border-b border-gray-100">
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-32"></div>
            <div className="h-3 bg-gray-200 rounded w-16"></div>
          </div>
          <div className="flex space-x-20">
            <div className="w-20 text-center">
              <div className="h-4 bg-gray-200 rounded w-12 mx-auto"></div>
            </div>
            <div className="w-20 text-center">
              <div className="h-4 bg-gray-200 rounded w-12 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
));

// Enhanced header component with tooltips and totals
const TableHeader = memo(
  ({ totalItems = 0, totalDiscount = 0, totalCommission = 0 }) => (
    <div className="mb-4 mt-2">
      <div className="flex justify-start md:justify-end items-center mb-2">
        <div className="flex space-x-20">
          <span className="text-sm font-medium text-gray-600 w-20 text-center">
            Discount
          </span>
          <span className="text-sm font-medium text-gray-600 w-20 text-center whitespace-nowrap">
            Commission
          </span>
        </div>
      </div>
      {totalItems > 0 && (
        <div className="flex justify-start md:justify-end border-b border-gray-200 pb-2">
          <div className="flex space-x-20">
            <div className="w-20 text-center">
              <span className="text-sm font-semibold text-gray-800">
                ${totalDiscount.toFixed(2)}
              </span>
            </div>
            <div className="w-20 text-center">
              <span className="text-sm font-semibold text-gray-800">
                ${totalCommission.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
);

// Enhanced item row with better error handling and formatting
const ItemRow = memo(({ item, index }) => {
  const ticketName = useMemo(() => {
    try {
      return lngDetect(item?.ticketInfo?.name) || "N/A";
    } catch (error) {
      console.warn("Error detecting language for ticket name:", error);
      return item?.ticketInfo?.name || "N/A";
    }
  }, [item?.ticketInfo?.name]);

  const price = useMemo(() => {
    const rawPrice = item?.ticketInfo?.price;
    return typeof rawPrice === "number" ? rawPrice.toFixed(2) : "0.00";
  }, [item?.ticketInfo?.price]);

  const discount = useMemo(() => {
    const rawDiscount = item?.totalPromoterDiscount;
    return typeof rawDiscount === "number" ? rawDiscount.toFixed(2) : "0.00";
  }, [item?.totalPromoterDiscount]);

  const commission = useMemo(() => {
    const rawCommission = item?.totalPromoterCommission;
    return typeof rawCommission === "number"
      ? rawCommission.toFixed(2)
      : "0.00";
  }, [item?.totalPromoterCommission]);

  return (
    <div
      key={item?._id || `item-${index}`}
      className="flex flex-col md:flex-row md:items-center gap-4 justify-between py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors duration-150"
    >
      <div className="flex-1 min-w-0">
        <div
          className="text-base font-medium text-gray-900 truncate"
          title={ticketName}
        >
          {ticketName}
        </div>
        <div className="text-sm text-gray-500 font-medium">${price}</div>
      </div>
      <div className="flex space-x-20 flex-shrink-0">
        <div className="w-20 text-center">
          <span
            className={`text-gray-600 px-2 py-1 rounded text-sm font-medium ${
              parseFloat(discount) > 0 ? "bg-green-50 text-green-700" : ""
            }`}
          >
            ${discount}
          </span>
        </div>
        <div className="w-20 text-center">
          <span
            className={`text-sm font-medium ${
              parseFloat(commission) > 0 ? "text-blue-600" : "text-gray-600"
            }`}
          >
            ${commission}
          </span>
        </div>
      </div>
    </div>
  );
});

// Enhanced custom hook with better error handling and caching
const usePromoterData = (promoter, ticketType) => {
  const filters = useMemo(
    () => ({
      limit: DEFAULT_LIMIT,
      page: DEFAULT_PAGE,
    }),
    []
  );

  const queryKey = useMemo(
    () => [
      `promoter-order-${ticketType}`,
      filters,
      promoter?.userData?._id,
      promoter?.eventData?._id,
    ],
    [filters, promoter?.userData?._id, promoter?.eventData?._id, ticketType]
  );

  const queryFn = useCallback(async () => {
    if (!promoter?.eventData?._id || !promoter?.userData?._id) {
      throw new Error("Missing required promoter or event data");
    }

    try {
      const res = await organizationTeamItem({
        ...filters,
        event: promoter.eventData._id,
        promoter: promoter.userData._id,
        orderStatus: ["confirmed"],
        ticketType,
      });

      if (res?.docs?.status !== 200) {
        const errorMessage =
          res?.message || "An error occurred while fetching data";
        toast.warning(errorMessage);
        throw new Error(errorMessage);
      }

      return res?.docs || [];
    } catch (error) {
      console.error(`Error fetching ${ticketType} data:`, error);

      // Only show toast for network errors, not for expected errors
      if (!error.message?.includes("Missing required")) {
        toast.error(`Failed to fetch ${ticketType} data`);
      }

      throw error;
    }
  }, [filters, promoter?.eventData?._id, promoter?.userData?._id, ticketType]);

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!promoter?.eventData?._id && !!promoter?.userData?._id,
    staleTime: STALE_TIME,
    retry: RETRY_COUNT,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    onError: (error) => {
      console.error(`Query error for ${ticketType}:`, error);
    },
  });
};

// Enhanced content component with analytics
const ContentComponent = memo(({ promoter, ticketType, emptyMessage }) => {
  const { data, isLoading, error, isFetching } = usePromoterData(
    promoter,
    ticketType
  );

  const analytics = useMemo(() => {
    const items = data?.data || [];
    return {
      totalItems: items.length,
      totalDiscount: items.reduce(
        (sum, item) => sum + (parseFloat(item?.totalPromoterDiscount) || 0),
        0
      ),
      totalCommission: items.reduce(
        (sum, item) => sum + (parseFloat(item?.totalPromoterCommission) || 0),
        0
      ),
    };
  }, [data?.data]);

  if (isLoading) {
    return (
      <div>
        <TableHeader {...analytics} />
        <LoadingSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-8 space-y-2">
        <div className="text-sm text-red-500">Error loading {ticketType}</div>
        <div className="text-xs text-gray-400">{error.message}</div>
      </div>
    );
  }

  const items = data?.data || [];

  return (
    <div className="relative">
      {isFetching && !isLoading && (
        <div className="absolute top-0 right-0 z-10">
          <div className="text-xs text-blue-500 bg-blue-50 px-2 py-1 rounded">
            Updating...
          </div>
        </div>
      )}

      <TableHeader {...analytics} />

      {items.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <div className="text-sm">{emptyMessage}</div>
        </div>
      ) : (
        <div className="space-y-0">
          {items.map((item, index) => (
            <ItemRow
              key={item?._id || `${ticketType}-${index}`}
              item={item}
              index={index}
            />
          ))}
        </div>
      )}
    </div>
  );
});

// Event Tickets Content Component
const EventPassesContent = memo(({ promoter }) => (
  <ContentComponent
    promoter={promoter}
    ticketType="ticket"
    emptyMessage="No event tickets found"
  />
));

// Event Add-ons Content Component
const EventAddonContent = memo(({ promoter }) => (
  <ContentComponent
    promoter={promoter}
    ticketType="addons-event"
    emptyMessage="No add-ons found"
  />
));

// Enhanced error boundary component
const ErrorBoundary = ({ children, fallback }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <div className="text-center py-8 text-red-500">
        {fallback || "Something went wrong. Please try refreshing the page."}
      </div>
    );
  }

  return children;
};

// Main Component with enhanced error handling and performance
export default function PromoterCommission() {
  const { promoter } = useOutletContext();
  const [refreshKey, setRefreshKey] = useState(0);

  // Force refresh function
  const handleRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  // Memoize accordion data with dependency on refreshKey for manual refresh capability
  const accordionData = useMemo(
    () => [
      {
        title: (
          <div className="flex items-center justify-between w-full">
            <span className="text-lg font-medium text-gray-900">
              {t("Event Tickets")}
            </span>
          </div>
        ),
        content: (
          <ErrorBoundary fallback="Error loading event tickets">
            <EventPassesContent
              promoter={promoter?.data}
              key={`tickets-${refreshKey}`}
            />
          </ErrorBoundary>
        ),
        buttonClass:
          "!bg-white border border-gray-200 hover:!bg-gray-50 !py-4 !px-4 !text-base transition-colors duration-150",
        contentClass: "!px-4 !pb-4 !pt-0",
      },
      {
        title: (
          <div className="flex items-center justify-between w-full">
            <span className="text-lg font-medium text-gray-900">
              {t("Add Ons")}
            </span>
          </div>
        ),
        content: (
          <ErrorBoundary fallback="Error loading add-ons">
            <EventAddonContent
              promoter={promoter?.data}
              key={`addons-${refreshKey}`}
            />
          </ErrorBoundary>
        ),
        buttonClass:
          "!bg-white border border-gray-200 hover:!bg-gray-50 !py-4 !px-4 !text-base transition-colors duration-150",
        contentClass: "!px-4 !pb-4 !pt-2",
      },
    ],
    [promoter?.data, refreshKey]
  );

  // Enhanced loading state
  if (!promoter) {
    return (
      <div className="w-full p-6 bg-white">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-64"></div>
          <div className="space-y-2">
            <div className="h-16 bg-gray-200 rounded"></div>
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  // Early return if no promoter data
  if (!promoter?.data) {
    return (
      <div className="w-full p-6 bg-white">
        <div className="text-center py-8">
          <div className="text-gray-500 mb-4">
            {t("No promoter data available")}
          </div>
          <button
            onClick={handleRefresh}
            className="text-blue-600 hover:text-blue-800 text-sm underline"
          >
            {t("Try refreshing")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-6 bg-white">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">
          {t("Prices, Commissions and Discounts")}
        </h1>
      </div>

      <ErrorBoundary>
        <Accordian
          data={accordionData}
          className="!space-y-4 !rounded-none !bg-transparent"
          buttonClass="!rounded-lg"
        />
      </ErrorBoundary>
    </div>
  );
}
